"use client";

import React from "react";
import {
  HeartPulse,
  TestTubeDiagonal as TestTube,
  Leaf,
  MessageSquare,
  GraduationCap,
  ShieldCheck,
  CalendarDays,
  Sparkles,
} from "lucide-react";
import { useCart } from "../../data/CartContext";
import { track } from "@lib/track";
import { FAQ_ITEMS } from "./faqItems";

// -----------------------------------------------------------------------------
// Shared data
// -----------------------------------------------------------------------------

const CHECKOUT_URL = "/shop/cart";

const DATE_DATA = [
  { title: "Guided Cleanse - Intake 2", range: "May 18 – May 24", status: "Open", cart: "Guided Cleanse Intake 2 - May 18th", startDate: new Date("2026-05-18") },
  { title: "Guided Cleanse - Intake 3", range: "Jun 8 – Jun 14", status: "Open", cart: "Guided Cleanse Intake 3 - Jun 8th", startDate: new Date("2026-06-08") },
  { title: "Guided Cleanse - Intake 4", range: "Jul 6 – Jul 12", status: "Open", cart: "Guided Cleanse Intake 4 - Jul 6th", startDate: new Date("2026-07-06") },
];

const DATE_DATA_SELF = [
  { title: "6 Day Self Guided Cleanse", range: "Anytime", status: "Filling fast", cart: "6 Day Self Guided Cleanse" },
];

const INCLUDED = [
  { icon: <Leaf className="h-6 w-6" />, title: "Gourmet Cleanse", desc: "4 days of expertly designed juices & mineral‑rich broths to kickstart detox and energy." },
  { icon: <Sparkles className="h-6 w-6" />, title: "Nourishing Meals", desc: "2 transition days with simple, delicious meal‑prep recipes for a gentle refeed." },
  { icon: <TestTube className="h-6 w-6" />, title: "Premium Supplements", desc: "Targeted herbs & nutrients for liver, lymph & digestion—curated for the 6‑day protocol." },
  { icon: <MessageSquare className="h-6 w-6" />, title: "Daily Guidance", desc: "Private WhatsApp support + nightly Zoom calls with Niki to keep you on track." },
  { icon: <GraduationCap className="h-6 w-6" />, title: "In‑Depth Education", desc: "Detox science, toxic load reduction, parasite basics & choosing supplements with confidence." },
  { icon: <ShieldCheck className="h-6 w-6" />, title: "Post‑Cleanse Support", desc: "1‑month probiotics + Get Up & Go Cacao blend to maintain momentum after the cleanse." },
];

const TOPICS = [
  "The science of cleansing & how your body detoxes",
  "Easy ways to reduce everyday toxic load",
  "Optimising liver detox pathways naturally",
  "Identifying your primary health priorities",
  "Simple strategies to support healing",
  "Parasite cleansing: what, why, when",
  "How to choose supplements wisely",
];

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function FeaturePageClient() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-[var(--color-bg-muted)] via-white to-white text-slate-900">
      <ChunkOne />
      <ChunkTwo />
      <ChunkThree />
    </main>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 1 — Header + Hero + TrustBar
// -----------------------------------------------------------------------------
function ChunkOne() {
  return (
    <>
      <Header />
      <Hero />
      <TrustBar />
    </>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-[var(--color-bg-muted)] border-b border-[var(--color-accent)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-[var(--color-brand)]" />
          <span className="font-semibold tracking-tight">Niki Charnock <span className="text-[var(--color-accent)]">• Live 6-Day Guided Reset | Anytime Self-Paced Reset</span></span>
        </div>
        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-700">
          <a href="#program" className="hover:text-[var(--color-brand)]">Program</a>
          <a href="#topics" className="hover:text-[var(--color-brand)]">Zoom Topics</a>
          <a href="#dates" className="hover:text-[var(--color-brand)]">Dates</a>
          <a href="#faq" className="hover:text-[var(--color-brand)]">FAQ</a>
        </nav>
        <a href="#dates" className="inline-flex items-center gap-2 rounded-full bg-[var(--color-brand)] px-4 py-2 text-white text-sm shadow">Buy Now</a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative">
      <div className="absolute inset-0 -z-10"><PatternBG /></div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Elevate Your Wellness:
            <br /><span className="text-[var(--color-brand)]">A 6‑Day Guided Cleanse</span>
          </h1>
          <p className="mt-5 text-lg text-slate-900 max-w-xl">More than detox—this immersive programme supports your gut, liver & whole‑body health with expert guidance, premium supplements and practical education.</p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {[
              "Expert‑led by Naturopathic Nutritionalist Niki Charnock",
              "Daily WhatsApp + live Zoom",
              "Juice, broth, meals & post‑cleanse plan",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2 rounded-xl bg-[var(--color-bg-muted)]/70 p-3 shadow-sm ring-1 ring-[var(--color-accent)]"><HeartPulse className="h-4 w-4 text-[var(--color-brand)]" /> {item}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#dates"
              onClick={() => {
                track(
                  "cta_click",
                  { label: "Hero: See Dates", component: "Hero" },
                  { event: "cta_click", label: "Hero: See Dates", component: "Hero" }
                );
              }}
              className="inline-flex items-center justify-center rounded-full border border-[var(--color-brand)] px-6 py-3 font-medium hover:bg-[var(--color-brand)] hover:text-white"
            >
              See Dates
            </a>
          </div>
          <p className="mt-3 text-sm text-slate-600">3 upcoming guided dates — limited spots per Intake.</p>
        </div>
        <div className="relative flex justify-center">
          <div className="absolute -inset-6 -z-10 rounded-3xl bg-indigo-100 blur-2xl" />
          <div className="text-center p-5">
            <img
              src="/images/retreats/nikiFood.webp"
              alt="Guided Cleanse"
              className="h-3/4 w-3/4 object-cover rounded-full"
            />
            <p className="text-sm text-slate-600 mt-5">Guided by</p>
            <p className="font-semibold text-[var(--color-brand)]">Niki Charnock</p>
            <p className="text-sm text-slate-600">Naturopathic Nutritionalist</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="py-6 border-y border-[var(--color-accent)] bg-[var(--color-bg-muted)]/60">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-slate-600">
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--color-brand)]" /> Evidence‑informed</div>
        <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[var(--color-brand)]" /> 6 days, live support</div>
        <div className="flex items-center gap-2"><Leaf className="h-4 w-4 text-[var(--color-brand)]" /> Whole‑food focused</div>
        <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-[var(--color-brand)]" /> Gentle & sustainable</div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 2 — Core Content Sections
// -----------------------------------------------------------------------------
function ChunkTwo() {
  return (
    <>
      <IncludedSection />
      <TopicsSection />
      <DatesSection />
      <SelfSection />
      <PricingSimple />
    </>
  );
}

function IncludedSection() {
  return (
    <section id="program" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="What you'll receive" title="A complete, guided reset" subtitle="Everything you need to support gut, liver & whole‑body health—plus the education to keep results going." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {INCLUDED.map((item) => (
            <div key={item.title} className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-brand-hover)]">{item.icon}</div>
              <h3 className="font-semibold text-lg text-[var(--color-brand)]">{item.title}</h3>
              <p className="mt-2 text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TopicsSection() {
  return (
    <section id="topics" className="py-16 sm:py-20 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Live Zoom curriculum" title="What you'll learn each evening" subtitle="Short, practical sessions to empower you with tools for lifelong wellness." />
        <div className="mx-auto max-w-3xl">
          <ul className="grid sm:grid-cols-2 gap-3">
            {TOPICS.map((t) => (
              <li key={t} className="flex items-start gap-3 rounded-xl bg-[var(--color-bg-muted)] p-4 ring-1 ring-[var(--color-accent)] shadow-sm">
                <div className="mt-1 h-2.5 w-2.5 rounded-full bg-[var(--color-brand)]" />
                <span className="text-slate-700">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function DatesSection() {
  const { addToCart } = useCart();
  const now = new Date();
  const futureDates = DATE_DATA.filter((d) => d.startDate > now);

  if (futureDates.length === 0) return null;

  const handleAddToCart = (date: { title: string; range: string; status: string; cart?: string }) => {
    const cartItem = {
      id: `guided-cleanse-${date.range}`,
      title: `Guided Cleanse - Start Date: ${date.range}`,
      price: "£260",
      qty: 1,
      totalPrice: 260,
    };
    addToCart(cartItem);
    track(
      "add_to_cart",
      { label: `Added to Cart: ${date.title}`, component: "DatesSection" },
      {
        event: "add_to_cart",
        label: `Added to Cart: ${date.title}`,
        component: "DatesSection",
        meta: { range: date.range, status: date.status },
      }
    );
    window.location.href = CHECKOUT_URL;
  };

  return (
    <section id="dates" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Upcoming"
          title="Choose your guided start date"
          subtitle="Limited spaces for high‑touch support. Secure your spot now."
        />
        <div className="grid md:grid-cols-3 gap-3">
          {futureDates.map((d) => (
            <div
              key={d.title}
              className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[var(--color-brand)]">{d.title}</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                  <CalendarDays className="h-3.5 w-3.5" /> {d.status}
                </span>
              </div>
              <h3 className="mt-2 text-2xl font-semibold">{d.range}</h3>
              <button
                onClick={() => handleAddToCart(d)}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[var(--color-brand)] px-4 py-2.5 text-[var(--btn-anchor-text)] font-medium shadow hover:bg-[var(--btn-anchor-hover)]"
              >
                Buy for this date
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SelfSection() {
  const { addToCart } = useCart();

  const handleAddToCart = () => {
    const cartItem = {
      id: "self-guided-cleanse",
      title: `6 Day Self Guided Cleanse`,
      price: "£260",
      qty: 1,
      totalPrice: 260,
    };
    addToCart(cartItem);
    track(
      "add_to_cart",
      { label: "Added to Cart: Self-Guided Cleanse", component: "SelfSection" },
      { event: "add_to_cart", label: "Added to Cart: Self-Guided Cleanse", component: "SelfSection" }
    );
    window.location.href = CHECKOUT_URL;
  };

  return (
    <section id="self-paced" className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Anytime"
          title="Take the cleanse at your own pace and start-time"
          subtitle="Perfect for busy schedules or those who prefer self‑paced learning."
        />
        <div className="grid md:grid-cols-4 gap-3">
          {DATE_DATA_SELF.map((d) => (
            <div
              key={d.title}
              className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-[var(--color-brand)]">{d.title}</p>
              </div>
              <h3 className="mt-2 text-2xl font-semibold">{d.range}</h3>
              <button
                onClick={() => handleAddToCart()}
                className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[var(--color-brand)] px-4 py-2.5 text-[var(--btn-anchor-text)] font-medium shadow hover:bg-[var(--btn-anchor-hover)]"
              >
                Buy Now
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSimple() {
  return (
    <section className="py-16 sm:py-20 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Investment" title="Everything included—no add‑ons required" subtitle="Quality ingredients & professional support are bundled for a seamless experience." />
        <div className="mx-auto max-w-4xl grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-[var(--color-bg-muted)] p-8 ring-1 ring-[var(--color-accent)] shadow-sm">
            <p className="text-sm font-medium text-slate-700">Guided Intake</p>
            <div className="mt-2 flex items-end gap-2">
              <span className="text-4xl font-extrabold tracking-tight">£260</span>
            </div>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              <li>• Gourmet juices & broths (4 days)</li>
              <li>• Transition meal‑prep plan (2 days)</li>
              <li>• 6‑day supplement pack (liver, lymph, digestion)</li>
              <li>• Private WhatsApp + nightly Zoom with Niki</li>
              <li>• 1‑month probiotics + Get Up & Go Cacao</li>
              <li>• Post‑cleanse plan & resources</li>
            </ul>
            <a href="#dates" className="mt-8 inline-flex w-full items-center justify-center rounded-full bg-[var(--color-brand)] px-5 py-3 text-white font-medium shadow hover:border hover:border-[var(--color-brand)] hover:text-[var(--color-brand)] hover:bg-transparent">Buy Now</a>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-brand)] p-0.5 shadow">
            <div className="rounded-2xl bg-[var(--color-bg-muted)] p-8">
              <p className="text-sm font-medium text-slate-700">Why it works</p>
              <h3 className="mt-2 text-xl font-semibold">Comprehensive, not extreme</h3>
              <p className="mt-3 text-slate-700 text-sm">This cleanse gently supports both phases of liver detoxification while maintaining gut integrity. You'll transition on and off with real food, ensuring sustainable results without rebound.</p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <Badge>Science‑backed</Badge>
                <Badge>High‑touch coaching</Badge>
                <Badge>Premium ingredients</Badge>
                <Badge>Beginner‑friendly</Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// CHUNK 3 — Testimonials + FAQ + CTA
// -----------------------------------------------------------------------------
function ChunkThree() {
  return (
    <>
      <Testimonials />
      <FAQ />
      <CTA />
    </>
  );
}

function Testimonials() {
  const items = [
    { quote: "I have done several heavy metal liver and gut cleanses with Niki. I always feel amazing afterwards ans notice my inflammation and Psoriasis improve over the week. She has made a something that seems quite overwhelming and complicated, very accessible and simple!", name: "Johnathan" },
    { quote: "The gut cleanse was unreal, The gut cleanse was unreal, felt so much better after & losing a few kgs was a great side note. Every bit of advice from Nic has made me feel healthier 🫶🏼✨", name: "Helen" },
    { quote: "The learning how to make the foods I need to support my gut and liver health has been invaluable and something I can incorporate into my regular nutrition. I have learned so much from Niki's expert guidance and the additional live sessions she provides as part of the support for the programme. I would highly recommend to anyone considering a reboot to provide the foundations and momentum for changes in nutritional habits.", name: "Claire" },
  ];
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Results" title="What recent participants are saying" />
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t) => (
            <blockquote key={t.name} className="rounded-2xl bg-[var(--color-bg-muted)] p-6 ring-1 ring-[var(--color-accent)] shadow-sm">
              <p className="text-slate-700">"{t.quote}"</p>
              <footer className="mt-4 text-sm font-medium">{t.name}</footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = FAQ_ITEMS;
  return (
    <section id="faq" className="py-16 sm:py-20 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading title="Frequently asked questions" />
        <div className="mx-auto max-w-3xl divide-y divide-[var(--color-accent)] rounded-2xl bg-[var(--color-bg-muted)] ring-1 ring-[var(--color-accent)]">
          {items.map((item, i) => (
            <details key={i} className="group p-6 open:bg-[var(--color-bg-sage-light)]/20">
              <summary className="flex cursor-pointer list-none items-center justify-between text-left font-medium">
                <span>{item.q}</span>
                <svg className="h-5 w-5 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" /></svg>
              </summary>
              <p className="mt-3 text-slate-700 text-sm">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="py-16">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl bg-gradient-to-tr from-indigo-600 to-[var(--color-accent)] p-8 sm:p-12 text-white shadow">
          <div className="absolute -inset-16 -z-10 opacity-30"><PatternBG dark /></div>
          <div className="max-w-3xl">
            <h3 className="text-3xl font-bold">Ready for a full‑body reset?</h3>
            <p className="mt-2 text-indigo-100">Join a small, supportive Intake and complete your reset with expert guidance.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#dates" className="inline-flex items-center justify-center rounded-full border text-white border-white px-6 py-3 font-semibold hover:bg-white hover:text-[var(--color-brand)]">Choose a Date</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function SectionHeading({ eyebrow, title, subtitle }: { eyebrow?: string; title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-10">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-widest text-[var(--color-brand)]">{eyebrow}</p>}
      <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>
      {subtitle && <p className="mt-3 max-w-2xl mx-auto text-slate-700">{subtitle}</p>}
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand-hover)] px-3 py-1 text-xs font-medium text-slate-700">{children}</span>;
}

function PatternBG({ dark = false }: { dark?: boolean }) {
  return (
    <svg aria-hidden className={`h-full w-full ${dark ? "text-white" : "text-indigo-600"}`} xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
      <defs>
        <pattern id="hex" width="40" height="40" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
          <rect width="2" height="40" fill="currentColor" opacity="0.08" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#hex)" />
    </svg>
  );
}
